import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  ArrowUpRight,
  Building2,
  Target,
  Download
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

import CommissionCalculator from '../components/CommissionCalculator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#1B365D', '#0071E3', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const COMMISSION_PRODUCTS = [
  { value: 'EHR', label: 'EHR (Electronic Health Records)' },
  { value: 'PMS', label: 'Practice Management System' },
  { value: 'RCM', label: 'Revenue Cycle Management' },
  { value: 'CCM', label: 'Chronic Care Management' },
  { value: 'RPM', label: 'Remote Patient Monitoring' },
  { value: 'APCM', label: 'Advanced Primary Care Management' },
  { value: 'AWV', label: 'Annual Wellness Visit' },
];

const Commissions = () => {
  const { getAuthHeaders, isAdmin } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchCommissions();
    fetchSummary();
  }, [statusFilter]);

  const fetchCommissions = async () => {
    try {
      let url = `${API_URL}/api/commissions`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const response = await axios.get(url, getAuthHeaders());
      setCommissions(response.data);
    } catch (error) {
      console.error('Failed to fetch commissions:', error);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/commissions/summary`, getAuthHeaders());
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch commission summary:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const productData = summary?.by_product 
    ? Object.entries(summary.by_product).map(([name, data]) => ({
        name,
        amount: data.amount,
        count: data.count
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-500">Loading commissions...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-8"
      data-testid="commissions-page"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1B365D] tracking-tight">Commissions</h1>
          <p className="text-slate-500 mt-1">Track your earnings and commission payouts</p>
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            className="rounded-full"
            onClick={async () => {
              try {
                const response = await axios.get(`${API_URL}/api/export/commissions`, {
                  ...getAuthHeaders(),
                  responseType: 'blob',
                });
                const blob = new Blob([response.data], { type: 'text/csv' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = 'commissions_export.csv';
                link.click();
                URL.revokeObjectURL(link.href);
                toast.success('Commissions exported to CSV');
              } catch {
                toast.error('Failed to export commissions');
              }
            }}
            data-testid="export-commissions-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Earned</p>
                <p className="mt-2 text-3xl font-bold text-[#1B365D]">
                  ${(summary?.total_earned || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-[#1B365D]/10 rounded-xl">
                <DollarSign className="w-6 h-6 text-[#1B365D]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Pending</p>
                <p className="mt-2 text-3xl font-bold text-amber-600">
                  ${(summary?.total_pending || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Approved</p>
                <p className="mt-2 text-3xl font-bold text-blue-600">
                  ${(summary?.total_approved || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-apple">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Paid Out</p>
                <p className="mt-2 text-3xl font-bold text-emerald-600">
                  ${(summary?.total_paid || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="history" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-full">
          <TabsTrigger value="history" className="rounded-full data-[state=active]:bg-white">
            Commission History
          </TabsTrigger>
          <TabsTrigger value="calculator" className="rounded-full data-[state=active]:bg-white">
            Calculator
          </TabsTrigger>
          <TabsTrigger value="structure" className="rounded-full data-[state=active]:bg-white">
            Commission Structure
          </TabsTrigger>
        </TabsList>

        {/* Commission History Tab */}
        <TabsContent value="history" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Commission Chart */}
            <Card className="border-0 shadow-apple lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg text-[#1B365D]">Earnings by Product</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={productData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                      <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${v}`} />
                      <Tooltip 
                        formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']}
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                      />
                      <Bar dataKey="amount" fill="#1B365D" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Distribution Pie Chart */}
            <Card className="border-0 shadow-apple">
              <CardHeader>
                <CardTitle className="text-lg text-[#1B365D]">Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={productData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="amount"
                      >
                        {productData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 mt-2 justify-center">
                  {productData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-1 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-slate-600">{entry.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Commission List */}
          <Card className="border-0 shadow-apple">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-[#1B365D]">Commission Records</CardTitle>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Lead</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Product</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Type</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Amount</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Status</th>
                      <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-4 px-4">Payout Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {commissions.map((commission) => (
                      <tr key={commission.id} className="hover:bg-slate-50" data-testid={`commission-row-${commission.id}`}>
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-[#1B365D]" />
                            </div>
                            <span className="font-medium text-slate-900">{commission.lead_name?.startsWith('ENC:') ? 'Encrypted Lead' : commission.lead_name}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge variant="outline">{commission.product || 'N/A'}</Badge>
                        </td>
                        <td className="py-4 px-4 text-slate-600 capitalize">
                          {commission.commission_type?.replace('_', ' ')}
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-semibold text-emerald-600">
                            ${commission.amount.toLocaleString()}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={getStatusColor(commission.status)}>
                            {commission.status}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-slate-500 text-sm">
                          {commission.payout_date 
                            ? new Date(commission.payout_date).toLocaleDateString() 
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {commissions.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    No commission records found.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calculator Tab */}
        <TabsContent value="calculator">
          <CommissionCalculator />
        </TabsContent>

        {/* Commission Structure Tab */}
        <TabsContent value="structure">
          <Card className="border-0 shadow-apple">
            <CardHeader>
              <CardTitle className="text-lg text-[#1B365D]">Commission Structure</CardTitle>
              <CardDescription>
                Understand how commissions are calculated for each product
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {summary?.commission_structure && Object.entries(summary.commission_structure).map(([product, structure]) => (
                  <div 
                    key={product}
                    className="p-4 border border-slate-200 rounded-xl hover:border-[#1B365D]/30 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-[#1B365D]/10 flex items-center justify-center">
                        <Target className="w-5 h-5 text-[#1B365D]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">{product}</h4>
                        {structure.flat_fee > 0 && (
                          <p className="text-sm text-emerald-600">Flat fee: ${structure.flat_fee}</p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">{structure.description}</p>
                    {structure.payout_delay_days > 0 && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>Payout: {structure.payout_delay_days} days after Go Live</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
};

export default Commissions;
